/**
 * Tune page SEO helpers — keep behavior aligned with backend/utils/tunePageSeo.js (crawler SSR).
 */

import { collectTuneNotationAssetIds, tuneNotationLegacyImageUrl } from './tunePageMedia';

const DEFAULT_OG_PATH = '/images/collage9.jpg';

function trimStr(v) {
  return v != null && String(v).trim() ? String(v).trim() : '';
}

/**
 * Rich meta description: DB description plus tune_type, region, composer/artist when not redundant.
 */
export function buildTuneSeoDescription(title, description, meta) {
  const m = meta && typeof meta === 'object' ? meta : {};
  const t = trimStr(title);
  const d0 = trimStr(description);
  const bits = [];
  if (trimStr(m.tune_type)) bits.push(trimStr(m.tune_type));
  if (trimStr(m.region)) bits.push(trimStr(m.region));
  const comp = trimStr(m.composer) || trimStr(m.artist);
  if (comp) bits.push(comp);
  const facetStr = bits.join(' · ');

  if (d0) {
    if (!facetStr) return d0;
    const lower = d0.toLowerCase();
    const redundant = bits.length > 0 && bits.every((b) => !b || lower.includes(b.toLowerCase()));
    if (redundant) return d0;
    return `${d0.replace(/\s*\.?\s*$/, '')} — ${facetStr}.`;
  }
  if (facetStr) {
    return `${t || 'Celtic fiddle tune'} — ${facetStr}. Notation, audio, and related tunes at Cranford Publications.`;
  }
  return `Read ${t || 'this tune'} on Cranford Publications — Celtic fiddle music.`;
}

/** Merge tag list with tune facets and site terms. */
export function buildTuneSeoKeywords(meta, tagArray) {
  const m = meta && typeof meta === 'object' ? meta : {};
  const tags = Array.isArray(tagArray) ? tagArray.map((x) => trimStr(x)).filter(Boolean) : [];
  const fromMeta = [
    m.tune_type,
    m.region,
    m.composer,
    m.artist,
    m.collection_book,
    m.book || m.recording_album,
    m.key_signature || m.key,
  ]
    .map(trimStr)
    .filter(Boolean);
  const core = ['Cranford Publications', 'Celtic fiddle', 'traditional music', 'ABC notation'];
  const seen = new Set();
  const out = [];
  for (const s of [...tags, ...fromMeta, ...core]) {
    const k = s.toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      out.push(s);
    }
  }
  return out.join(', ');
}

/**
 * Absolute OG image: page og_image → first notation /api/assets/:id/thumbnail → legacy notation URL → default collage.
 */
export function resolveTuneOgImageUrl(baseUrl, pageOgImage, meta) {
  const base = String(baseUrl || '').replace(/\/$/, '') || 'https://www.cranfordpub.ca';
  const pog = trimStr(pageOgImage);
  if (pog) {
    if (/^https?:\/\//i.test(pog)) return pog;
    if (pog.startsWith('/')) return `${base}${pog}`;
    return `${base}/${pog.replace(/^\/+/, '')}`;
  }
  const m = meta && typeof meta === 'object' ? meta : {};
  const ids = collectTuneNotationAssetIds(m);
  if (ids.length > 0) {
    return `${base}/api/assets/${ids[0]}/thumbnail`;
  }
  const legacy = tuneNotationLegacyImageUrl(m);
  if (legacy) {
    if (/^https?:\/\//i.test(legacy)) return legacy;
    if (legacy.startsWith('/')) return `${base}${legacy}`;
    return `${base}/${legacy.replace(/^\/+/, '')}`;
  }
  return `${base}${DEFAULT_OG_PATH}`;
}
